import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

import { SupportApiService } from '../core/support-api.service';
import {
  AlertSettings, CreateVarPrincipalPayload, PartnerOrgGrant, UpdatePartnerPayload, PartnerSummary, VarPrincipalSummary,
} from '../core/models';

/** Create/disable VAR-partner provisioning (the follow-on the read-only launch
 * screen deferred). `oid` is asked for explicitly even though it's the least
 * obvious field on this form: it is the Entra Object ID of the partner's
 * already-provisioned CIAM external identity (that identity is stood up
 * out-of-band, same as a bt_staff account) — without it the row could never
 * authenticate, so it is a required input here, not something this screen
 * invents. screening_verified_at is the operator's attestation that the
 * out-of-band CJIS screening was verified for this specific individual and is
 * therefore never pre-filled or defaulted.
 *
 * Form fields are signal-backed (not plain properties), matching
 * EscalateModalComponent/ComposeComponent's documented reason: a signal write
 * notifies the template's two-way `[(ngModel)]` binding synchronously even
 * when set directly on the component instance outside a zone-tracked event
 * (as this component's spec does), avoiding a false-positive NG0100. */
@Component({
  selector: 'app-var-principal-review',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbTooltip],
  template: `
    <div class="bt-page">
      <div class="bt-page__head">
        <h1 class="bt-page__title">VAR Access</h1>
        <p class="bt-page__subtitle">
          Partner (VAR) accounts and their entitlements.
        </p>
      </div>

      <div class="bt-panel var-panel">
        <div class="var-panel__actions">
          @if (!showCreateForm) {
            <button type="button" class="bt-btn bt-btn--primary bt-btn--sm" data-testid="provision-var-button"
                    (click)="openCreateForm()"
                    ngbTooltip="Provision a new partner (VAR) account — requires the partner's CIAM identity and a verified CJIS screening date">
              + Create VAR partner
            </button>
          }
        </div>

        @if (showCreateForm) {
          <form class="create-var-form" data-testid="create-var-form" (ngSubmit)="submitCreate()">
            <div class="create-var-form__grid">
              <div class="bt-field">
                <label class="bt-label" ngbTooltip="The Entra Object ID of the partner's already-provisioned CIAM external identity">Entra object ID (oid)</label>
                <input class="bt-input" data-testid="create-var-oid" [(ngModel)]="oid" name="oid" required
                       placeholder="e.g. 3fa2c1e0-…" />
              </div>
              <div class="bt-field">
                <label class="bt-label" ngbTooltip="Which reseller/partner this account belongs to. The partner must already exist below (Partners & Org Access) — the orgs it can act on are managed there, not here.">Partner org key</label>
                <input class="bt-input" data-testid="create-var-partner-key" [(ngModel)]="partnerOrgKey" name="partnerOrgKey" required />
              </div>
              <div class="bt-field">
                <label class="bt-label" ngbTooltip="The partner's sign-in email">Email</label>
                <input class="bt-input" type="email" data-testid="create-var-email" [(ngModel)]="email" name="email" required />
              </div>
              <div class="bt-field">
                <label class="bt-label" ngbTooltip="Optional — shown in the roster table">Display name</label>
                <input class="bt-input" data-testid="create-var-display-name" [(ngModel)]="displayName" name="displayName" />
              </div>
              <div class="bt-field">
                <label class="bt-label" ngbTooltip="Required — the date the out-of-band CJIS background screening was verified for this individual. Never auto-filled.">Screening verified date</label>
                <input class="bt-input" type="date" data-testid="create-var-screening-date" [(ngModel)]="screeningVerifiedAt" name="screeningVerifiedAt" required />
              </div>
              <div class="bt-field">
                <label class="bt-label" ngbTooltip="The contract this access is tied to (optional)">Contract ref</label>
                <input class="bt-input" data-testid="create-var-contract-ref" [(ngModel)]="contractRef" name="contractRef" />
              </div>
              <div class="bt-field">
                <label class="bt-label" ngbTooltip="Optional — when this partner's entitlement expires">Active until</label>
                <input class="bt-input" type="date" data-testid="create-var-active-until" [(ngModel)]="activeUntil" name="activeUntil" />
              </div>
            </div>

            @if (createError) {
              <p class="bt-badge bt-badge--danger create-var-form__error" data-testid="create-var-error">{{ createError }}</p>
            }

            <div class="create-var-form__actions">
              <button type="submit" class="bt-btn bt-btn--primary bt-btn--sm" data-testid="create-var-submit"
                      [disabled]="!canSubmitCreate() || creating"
                      ngbTooltip="Provision this partner account">
                {{ creating ? 'Creating…' : 'Create partner' }}
              </button>
              <button type="button" class="bt-btn bt-btn--ghost bt-btn--sm" data-testid="create-var-cancel" (click)="closeCreateForm()">
                Cancel
              </button>
            </div>
          </form>
        }

        @if (loading) {
          <div class="bt-empty"><span class="bt-spinner"></span><span class="bt-empty__hint">Loading VAR accounts…</span></div>
        } @else if (!principals.length) {
          <div class="bt-empty">
            <span class="bt-empty__icon">🔑</span>
            <span class="bt-empty__title">No VAR accounts</span>
            <span class="bt-empty__hint">No partner principals are provisioned yet.</span>
          </div>
        } @else {
          <table class="bt-table" role="table" aria-label="VAR principals" data-testid="var-principal-table">
            <thead>
              <tr>
                <th ngbTooltip="The partner's sign-in email">Email</th>
                <th ngbTooltip="The contract this access is tied to">Contract</th>
                <th ngbTooltip="Computed client-side from active_until vs now">Entitlement</th>
                <th ngbTooltip="Whether background screening has been verified for this principal">Screening</th>
                <th ngbTooltip="Revoke this partner's ability to sign in">Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (p of principals; track p.id) {
                <tr [attr.data-testid]="'var-principal-row-' + p.id">
                  <td class="email">{{ p.email }}</td>
                  <td class="mono">{{ p.contract_ref ?? '—' }}</td>
                  <td [attr.data-testid]="'entitlement-status-' + p.id">
                    <span class="bt-badge" [class]="entitlementBadge(p)"
                          ngbTooltip="Computed from active_until — not a server-computed state, since active_until is exposed as-is">
                      {{ entitlementStatus(p) }}
                    </span>
                  </td>
                  <td>
                    @if (p.screening_verified_at) {
                      <span class="bt-badge bt-badge--success">Verified</span>
                    } @else {
                      <span class="bt-badge bt-badge--warning">Not verified</span>
                    }
                  </td>
                  <td>
                    @if (!p.is_active) {
                      <span class="bt-badge bt-badge--neutral">Disabled</span>
                    } @else if (confirmingDisableId === p.id) {
                      <span class="disable-confirm">
                        <button type="button" class="bt-btn bt-btn--danger bt-btn--sm"
                                [attr.data-testid]="'disable-var-confirm-' + p.id"
                                (click)="confirmDisable(p)">Confirm disable</button>
                        <button type="button" class="bt-btn bt-btn--ghost bt-btn--sm" (click)="cancelDisable()">Cancel</button>
                      </span>
                    } @else {
                      <button type="button" class="bt-btn bt-btn--danger bt-btn--sm"
                              [attr.data-testid]="'disable-var-button-' + p.id"
                              (click)="startDisable(p)"
                              ngbTooltip="Revoke this partner's ability to sign in. Reversible only by re-provisioning.">
                        Disable
                      </button>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }
      </div>

      <div class="bt-panel partner-panel">
        <div class="partner-head">
          <div>
            <h2 class="partner-head__title">Partners & Org Access</h2>
            <p class="partner-head__subtitle">
              Each partner (reseller) can act on any number of customer orgs. Grant or revoke
              orgs per partner — no cross-pollination unless the same org is granted to more
              than one partner.
            </p>
          </div>
          @if (!showPartnerForm) {
            <button type="button" class="bt-btn bt-btn--primary bt-btn--sm" data-testid="create-partner-button"
                    (click)="openPartnerForm()"
                    ngbTooltip="Create a new partner (reseller). You then grant it the customer orgs its VAR accounts may act on.">
              + Create partner
            </button>
          }
        </div>

        @if (showPartnerForm) {
          <form class="create-var-form" data-testid="create-partner-form" (ngSubmit)="submitPartner()">
            <div class="create-var-form__grid">
              <div class="bt-field">
                <label class="bt-label" ngbTooltip="A short stable key identifying this reseller — must match the partner_org_key you give its VAR accounts above">Partner org key</label>
                <input class="bt-input" data-testid="create-partner-key" [(ngModel)]="newPartnerKey" name="newPartnerKey" required placeholder="e.g. reseller-alpha" />
              </div>
              <div class="bt-field">
                <label class="bt-label" ngbTooltip="Optional friendly name shown in this list">Display name</label>
                <input class="bt-input" data-testid="create-partner-name" [(ngModel)]="newPartnerName" name="newPartnerName" />
              </div>
            </div>
            @if (partnerError) {
              <p class="bt-badge bt-badge--danger create-var-form__error" data-testid="create-partner-error">{{ partnerError }}</p>
            }
            <div class="create-var-form__actions">
              <button type="submit" class="bt-btn bt-btn--primary bt-btn--sm" data-testid="create-partner-submit"
                      [disabled]="!newPartnerKey.trim() || creatingPartner" ngbTooltip="Create this partner">
                {{ creatingPartner ? 'Creating…' : 'Create partner' }}
              </button>
              <button type="button" class="bt-btn bt-btn--ghost bt-btn--sm" data-testid="create-partner-cancel" (click)="closePartnerForm()">Cancel</button>
            </div>
          </form>
        }

        @if (partnersLoading) {
          <div class="bt-empty"><span class="bt-spinner"></span><span class="bt-empty__hint">Loading partners…</span></div>
        } @else if (!partners.length) {
          <div class="bt-empty">
            <span class="bt-empty__icon">🤝</span>
            <span class="bt-empty__title">No partners</span>
            <span class="bt-empty__hint">Create a partner, then grant it the orgs its VARs may act on.</span>
          </div>
        } @else {
          <div class="partner-list" data-testid="partner-list">
            @for (pt of partners; track pt.partner_org_key) {
              <div class="partner-card" [attr.data-testid]="'partner-row-' + pt.partner_org_key">
                <div class="partner-card__head">
                  <span class="partner-card__name">{{ pt.display_name || pt.partner_org_key }}</span>
                  <span class="mono partner-card__key">{{ pt.partner_org_key }}</span>
                  @if (!pt.is_active) { <span class="bt-badge bt-badge--neutral">Inactive</span> }
                </div>
                <div class="org-grants">
                  @if (orgGrants[pt.partner_org_key]; as grants) {
                    @if (!grants.length) {
                      <span class="bt-empty__hint org-grants__empty" [attr.data-testid]="'no-org-grants-' + pt.partner_org_key">No org access yet — grant an org below.</span>
                    } @else {
                      @for (g of grants; track g.id) {
                        <span class="org-chip" [attr.data-testid]="'org-grant-' + g.id">
                          {{ g.organization_id }}
                          <button type="button" class="org-chip__x" [attr.data-testid]="'revoke-org-' + g.id" (click)="revokeOrg(pt.partner_org_key, g)" ngbTooltip="Revoke this org from the partner">×</button>
                        </span>
                      }
                    }
                  } @else {
                    <span class="bt-empty__hint org-grants__empty">Loading…</span>
                  }
                </div>
                <div class="grant-org-row">
                  <input class="bt-input grant-org-row__input" [attr.data-testid]="'grant-org-input-' + pt.partner_org_key"
                         [ngModel]="orgInputs[pt.partner_org_key] || ''" (ngModelChange)="orgInputs[pt.partner_org_key] = $event"
                         [name]="'orgInput-' + pt.partner_org_key" placeholder="Organization ID" ngbTooltip="The customer org id to grant this partner" />
                  <button type="button" class="bt-btn bt-btn--primary bt-btn--sm" [attr.data-testid]="'grant-org-button-' + pt.partner_org_key"
                          [disabled]="!(orgInputs[pt.partner_org_key] || '').trim()" (click)="grantOrg(pt.partner_org_key)" ngbTooltip="Grant this org to the partner">Grant org</button>
                </div>
                <div class="alert-row">
                  <label class="bt-label alert-row__label"
                         ngbTooltip="Where new-ticket alerts for this partner's customers are emailed. Alerts carry ticket metadata and a console link only — never customer text. Leave blank to send them to BlueTitan's default address.">Alert email</label>
                  <input class="bt-input alert-row__input" type="email" [attr.data-testid]="'partner-alert-email-' + pt.partner_org_key"
                         [ngModel]="alertEmailInputs[pt.partner_org_key] || ''" (ngModelChange)="alertEmailInputs[pt.partner_org_key] = $event"
                         [name]="'alertEmail-' + pt.partner_org_key" placeholder="e.g. support@partner.com"
                         ngbTooltip="Where new-ticket alerts for this partner's customers are emailed. Leave blank to use BlueTitan's default address." />
                  <label class="alert-row__cc" ngbTooltip="Also email BlueTitan's default address for this partner's customers' tickets, so BT can see whether a ticket goes unclaimed.">
                    <input type="checkbox" [attr.data-testid]="'partner-cc-bt-' + pt.partner_org_key"
                           [ngModel]="ccInputs[pt.partner_org_key] ?? true" (ngModelChange)="ccInputs[pt.partner_org_key] = $event"
                           [name]="'ccBt-' + pt.partner_org_key" />
                    CC BlueTitan
                  </label>
                  <button type="button" class="bt-btn bt-btn--primary bt-btn--sm" [attr.data-testid]="'partner-alert-save-' + pt.partner_org_key"
                          (click)="savePartnerAlert(pt.partner_org_key)" ngbTooltip="Save this partner's alert routing">Save</button>
                  @if (alertSaveError[pt.partner_org_key]) {
                    <span class="bt-badge bt-badge--danger">{{ alertSaveError[pt.partner_org_key] }}</span>
                  }
                </div>
              </div>
            }
          </div>
        }
      </div>
      <div class="bt-panel partner-panel" data-testid="default-alert-panel">
        <h2 class="partner-head__title">Default alert address</h2>
        <p class="partner-head__subtitle">New tickets from customers without a partner alert address — and every CC'd partner ticket — are emailed here.</p>
        <div class="alert-row">
          <input class="bt-input alert-row__input" type="email" data-testid="default-alert-email"
                 [(ngModel)]="defaultAlertInput" name="defaultAlertEmail"
                 ngbTooltip="BlueTitan's default alert address (a distribution group). Alerts carry ticket metadata and a console link only — never customer text." />
          <button type="button" class="bt-btn bt-btn--primary bt-btn--sm" data-testid="default-alert-save"
                  [disabled]="!alertSettings || !defaultAlertInput.trim()" (click)="saveDefaultAlert()"
                  ngbTooltip="Save the default alert address. Takes effect for the next ticket; already-queued alerts keep their address.">Save</button>
          @if (defaultAlertError) { <span class="bt-badge bt-badge--danger" data-testid="default-alert-error">{{ defaultAlertError }}</span> }
        </div>
      </div>
    </div>
  `,
  styles: [`
    .var-panel { padding: 0.5rem 1rem 1rem; }
    .var-panel__actions { display: flex; justify-content: flex-end; margin-bottom: 0.75rem; }
    .email { font-weight: 500; color: var(--bt-text-primary); }
    .mono { font-family: 'Roboto Mono', ui-monospace, monospace; font-size: 13px; color: var(--bt-text-secondary); }
    /* Explicit column widths + truncation so table-layout:fixed (shared .bt-table) sizes
       predictably for real (longer) emails/orgs, not just the short demo values. */
    .bt-table th:nth-child(1), .bt-table td:nth-child(1) { width: 30%; }
    .bt-table th:nth-child(2), .bt-table td:nth-child(2) { width: 18%; }
    .bt-table th:nth-child(3), .bt-table td:nth-child(3) { width: 18%; }
    .bt-table th:nth-child(4), .bt-table td:nth-child(4) { width: 16%; }
    .bt-table th:nth-child(5), .bt-table td:nth-child(5) { width: 18%; }
    .bt-table td { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .create-var-form { border: 1px solid var(--bt-border-default); border-radius: 8px; padding: 0.9rem; margin-bottom: 1rem; background: var(--bt-bg-surface); }
    .create-var-form__grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 0.75rem; }
    .create-var-form__actions { display: flex; gap: 0.5rem; margin-top: 0.85rem; }
    .create-var-form__error { margin: 0.6rem 0 0; white-space: normal; }
    .disable-confirm { display: inline-flex; gap: 0.4rem; }
    .partner-panel { padding: 0.5rem 1rem 1rem; margin-top: 1rem; }
    .partner-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; margin-bottom: 0.75rem; }
    .partner-head__title { margin: 0; font-size: 16px; color: var(--bt-text-primary); }
    .partner-head__subtitle { margin: 0.15rem 0 0; font-size: 13px; color: var(--bt-text-secondary); max-width: 60ch; }
    .partner-list { display: grid; gap: 0.75rem; }
    .partner-card { border: 1px solid var(--bt-border-default); border-radius: 8px; padding: 0.75rem 0.9rem; background: var(--bt-bg-surface); }
    .partner-card__head { display: flex; align-items: center; gap: 0.6rem; margin-bottom: 0.5rem; }
    .partner-card__name { font-weight: 600; color: var(--bt-text-primary); }
    .partner-card__key { font-size: 12px; color: var(--bt-text-secondary); }
    .org-grants { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-bottom: 0.5rem; }
    .org-grants__empty { font-style: italic; }
    .org-chip { display: inline-flex; align-items: center; gap: 0.35rem; padding: 0.15rem 0.5rem; border-radius: 999px; background: var(--bt-bg-muted, rgba(38,129,179,0.12)); color: var(--bt-text-primary); font-size: 13px; }
    .org-chip__x { border: none; background: none; cursor: pointer; color: var(--bt-text-secondary); font-size: 15px; line-height: 1; padding: 0; }
    .org-chip__x:hover { color: var(--bt-danger, #c0392b); }
    .grant-org-row { display: flex; gap: 0.5rem; align-items: center; }
    .grant-org-row__input { max-width: 260px; }
    .alert-row { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; margin-top: 0.5rem; }
    .alert-row__label { margin: 0; }
    .alert-row__input { max-width: 280px; }
    .alert-row__cc { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 13px; color: var(--bt-text-secondary); }
  `],
})
export class VarPrincipalReviewComponent implements OnInit {
  private api = inject(SupportApiService);
  principals: VarPrincipalSummary[] = [];
  loading = true;

  // Signal-backed (not plain properties) for the same NG0100 reason as the
  // form-field signals below: submitCreate()/confirmDisable() etc. are called
  // directly from component-instance test code, outside a zone-tracked
  // template event, and a signal write is the only mutation that reliably
  // notifies the `@if` consumer synchronously between two detectChanges() calls.
  private showCreateFormSignal = signal(false);
  get showCreateForm(): boolean { return this.showCreateFormSignal(); }
  set showCreateForm(value: boolean) { this.showCreateFormSignal.set(value); }

  private creatingSignal = signal(false);
  get creating(): boolean { return this.creatingSignal(); }
  set creating(value: boolean) { this.creatingSignal.set(value); }

  private createErrorSignal = signal<string | null>(null);
  get createError(): string | null { return this.createErrorSignal(); }
  set createError(value: string | null) { this.createErrorSignal.set(value); }

  private confirmingDisableIdSignal = signal<string | null>(null);
  get confirmingDisableId(): string | null { return this.confirmingDisableIdSignal(); }
  set confirmingDisableId(value: string | null) { this.confirmingDisableIdSignal.set(value); }

  private oidSignal = signal('');
  get oid(): string { return this.oidSignal(); }
  set oid(value: string) { this.oidSignal.set(value); }

  private partnerOrgKeySignal = signal('');
  get partnerOrgKey(): string { return this.partnerOrgKeySignal(); }
  set partnerOrgKey(value: string) { this.partnerOrgKeySignal.set(value); }

  private emailSignal = signal('');
  get email(): string { return this.emailSignal(); }
  set email(value: string) { this.emailSignal.set(value); }

  private displayNameSignal = signal('');
  get displayName(): string { return this.displayNameSignal(); }
  set displayName(value: string) { this.displayNameSignal.set(value); }

  // yyyy-mm-dd, the native <input type="date"> value shape.
  private screeningVerifiedAtSignal = signal('');
  get screeningVerifiedAt(): string { return this.screeningVerifiedAtSignal(); }
  set screeningVerifiedAt(value: string) { this.screeningVerifiedAtSignal.set(value); }

  private contractRefSignal = signal('');
  get contractRef(): string { return this.contractRefSignal(); }
  set contractRef(value: string) { this.contractRefSignal.set(value); }

  private activeUntilSignal = signal('');
  get activeUntil(): string { return this.activeUntilSignal(); }
  set activeUntil(value: string) { this.activeUntilSignal.set(value); }

  // --- Partners & Org Access (migration 010) --------------------------------
  partners: PartnerSummary[] = [];
  partnersLoading = true;
  orgGrants: Record<string, PartnerOrgGrant[] | undefined> = {};
  orgInputs: Record<string, string> = {};

  private showPartnerFormSignal = signal(false);
  get showPartnerForm(): boolean { return this.showPartnerFormSignal(); }
  set showPartnerForm(value: boolean) { this.showPartnerFormSignal.set(value); }
  private creatingPartnerSignal = signal(false);
  get creatingPartner(): boolean { return this.creatingPartnerSignal(); }
  set creatingPartner(value: boolean) { this.creatingPartnerSignal.set(value); }
  private partnerErrorSignal = signal<string | null>(null);
  get partnerError(): string | null { return this.partnerErrorSignal(); }
  set partnerError(value: string | null) { this.partnerErrorSignal.set(value); }
  private newPartnerKeySignal = signal('');
  get newPartnerKey(): string { return this.newPartnerKeySignal(); }
  set newPartnerKey(value: string) { this.newPartnerKeySignal.set(value); }
  private newPartnerNameSignal = signal('');
  get newPartnerName(): string { return this.newPartnerNameSignal(); }
  set newPartnerName(value: string) { this.newPartnerNameSignal.set(value); }

  alertEmailInputs: Record<string, string> = {};
  ccInputs: Record<string, boolean> = {};
  alertSaveError: Record<string, string | null> = {};
  alertSettings: AlertSettings | null = null;
  defaultAlertInput = '';
  defaultAlertError: string | null = null;

  ngOnInit(): void {
    this.refresh();
    this.refreshPartners();
    this.loadAlertSettings();
  }

  private refresh(): void {
    this.loading = true;
    this.api.listVarPrincipals().subscribe({
      next: (principals) => { this.principals = principals; this.loading = false; },
      error: () => { this.principals = []; this.loading = false; },
    });
  }

  entitlementStatus(p: VarPrincipalSummary): string {
    if (!p.active_until) return 'No expiry';
    return new Date(p.active_until) < new Date() ? 'Expired' : 'Active';
  }

  entitlementBadge(p: VarPrincipalSummary): string {
    if (!p.active_until) return 'bt-badge--neutral';
    return new Date(p.active_until) < new Date() ? 'bt-badge--danger' : 'bt-badge--success';
  }

  openCreateForm(): void {
    this.showCreateForm = true;
    this.createError = null;
  }

  closeCreateForm(): void {
    this.showCreateForm = false;
    this.createError = null;
    this.oid = '';
    this.partnerOrgKey = '';
    this.email = '';
    this.displayName = '';
    this.screeningVerifiedAt = '';
    this.contractRef = '';
    this.activeUntil = '';
  }

  canSubmitCreate(): boolean {
    return [this.oid, this.partnerOrgKey, this.email, this.screeningVerifiedAt]
      .every((f) => f.trim().length > 0);
  }

  submitCreate(): void {
    if (!this.canSubmitCreate() || this.creating) return;
    this.creating = true;
    this.createError = null;
    const payload: CreateVarPrincipalPayload = {
      oid: this.oid.trim(),
      partner_org_key: this.partnerOrgKey.trim(),
      email: this.email.trim(),
      display_name: this.displayName.trim() || null,
      screening_verified_at: this.toIsoDate(this.screeningVerifiedAt),
      contract_ref: this.contractRef.trim() || null,
      active_until: this.activeUntil ? this.toIsoDate(this.activeUntil) : null,
    };
    this.api.createVarPrincipal(payload).subscribe({
      next: (created) => {
        this.principals = [...this.principals, created].sort((a, b) => a.email.localeCompare(b.email));
        this.creating = false;
        this.closeCreateForm();
      },
      error: (err) => {
        this.creating = false;
        this.createError = err?.status === 409
          ? 'A VAR principal with this Entra object ID already exists.'
          : 'Could not create this VAR partner. Check the fields and try again.';
      },
    });
  }

  startDisable(p: VarPrincipalSummary): void {
    this.confirmingDisableId = p.id;
  }

  cancelDisable(): void {
    this.confirmingDisableId = null;
  }

  confirmDisable(p: VarPrincipalSummary): void {
    this.api.disableVarPrincipal(p.id).subscribe({
      next: (updated) => {
        this.principals = this.principals.map((row) => (row.id === updated.id ? updated : row));
        this.confirmingDisableId = null;
      },
      error: () => { this.confirmingDisableId = null; },
    });
  }

  private refreshPartners(): void {
    this.partnersLoading = true;
    this.api.listPartners().subscribe({
      next: (partners) => {
        this.partners = partners;
        for (const pt of this.partners) {
          this.alertEmailInputs[pt.partner_org_key] = pt.alert_email ?? '';
          this.ccInputs[pt.partner_org_key] = pt.cc_bluetitan;
        }
        this.partnersLoading = false;
        partners.forEach((p) => this.loadOrgGrants(p.partner_org_key));
      },
      error: () => { this.partners = []; this.partnersLoading = false; },
    });
  }

  private loadAlertSettings(): void {
    this.api.getAlertSettings().subscribe({
      next: (s) => { this.alertSettings = s; this.defaultAlertInput = s.default_alert_email; },
      error: () => { this.defaultAlertError = 'Could not load the default alert address.'; },
    });
  }

  savePartnerAlert(partnerOrgKey: string): void {
    this.alertSaveError[partnerOrgKey] = null;
    const payload: UpdatePartnerPayload = {
      alert_email: (this.alertEmailInputs[partnerOrgKey] ?? '').trim(),
      cc_bluetitan: this.ccInputs[partnerOrgKey] ?? true,
    };
    this.api.updatePartner(partnerOrgKey, payload).subscribe({
      next: (updated) => {
        this.partners = this.partners.map((p) => (p.partner_org_key === partnerOrgKey ? updated : p));
        this.alertEmailInputs[partnerOrgKey] = updated.alert_email ?? '';
        this.ccInputs[partnerOrgKey] = updated.cc_bluetitan;
      },
      error: () => { this.alertSaveError[partnerOrgKey] = 'Save failed — check the address and try again.'; },
    });
  }

  saveDefaultAlert(): void {
    if (!this.alertSettings) return;
    this.defaultAlertError = null;
    this.api.updateAlertSettings(this.defaultAlertInput.trim(), this.alertSettings.version).subscribe({
      next: (s) => { this.alertSettings = s; this.defaultAlertInput = s.default_alert_email; },
      error: (err) => {
        this.defaultAlertError = err?.status === 409
          ? 'Someone else changed this address — reload and try again.'
          : 'Save failed — check the address and try again.';
      },
    });
  }

  private loadOrgGrants(partnerOrgKey: string): void {
    this.api.listPartnerOrgGrants(partnerOrgKey).subscribe({
      next: (grants) => { this.orgGrants = { ...this.orgGrants, [partnerOrgKey]: grants }; },
      error: () => { this.orgGrants = { ...this.orgGrants, [partnerOrgKey]: [] }; },
    });
  }

  openPartnerForm(): void { this.showPartnerForm = true; this.partnerError = null; }

  closePartnerForm(): void {
    this.showPartnerForm = false; this.partnerError = null;
    this.newPartnerKey = ''; this.newPartnerName = '';
  }

  submitPartner(): void {
    if (!this.newPartnerKey.trim() || this.creatingPartner) return;
    this.creatingPartner = true; this.partnerError = null;
    this.api.createPartner({ partner_org_key: this.newPartnerKey.trim(), display_name: this.newPartnerName.trim() || null }).subscribe({
      next: (partner) => {
        if (!this.partners.some((p) => p.partner_org_key === partner.partner_org_key)) {
          this.partners = [...this.partners, partner].sort((a, b) => a.partner_org_key.localeCompare(b.partner_org_key));
        }
        this.orgGrants = { ...this.orgGrants, [partner.partner_org_key]: this.orgGrants[partner.partner_org_key] ?? [] };
        this.creatingPartner = false; this.closePartnerForm();
      },
      error: () => { this.creatingPartner = false; this.partnerError = 'Could not create this partner. Check the key and try again.'; },
    });
  }

  grantOrg(partnerOrgKey: string): void {
    const orgId = (this.orgInputs[partnerOrgKey] || '').trim();
    if (!orgId) return;
    this.api.grantPartnerOrg(partnerOrgKey, orgId).subscribe({
      next: (grant) => {
        const current = this.orgGrants[partnerOrgKey] ?? [];
        if (!current.some((g) => g.id === grant.id)) {
          this.orgGrants = { ...this.orgGrants, [partnerOrgKey]: [...current, grant] };
        }
        this.orgInputs = { ...this.orgInputs, [partnerOrgKey]: '' };
      },
      error: () => {},
    });
  }

  revokeOrg(partnerOrgKey: string, grant: PartnerOrgGrant): void {
    this.api.revokePartnerOrgGrant(grant.id).subscribe({
      next: () => {
        const current = this.orgGrants[partnerOrgKey] ?? [];
        this.orgGrants = { ...this.orgGrants, [partnerOrgKey]: current.filter((g) => g.id !== grant.id) };
      },
      error: () => {},
    });
  }

  private toIsoDate(value: string): string {
    // Parse the date-only input at LOCAL midnight (no 'Z'), not UTC midnight —
    // toISOString() then converts to UTC preserving the instant, so the calendar
    // day the operator picked survives round-trip in their own timezone. Forcing
    // UTC midnight shifted the day for non-UTC users (e.g. Mountain Time),
    // expiring active_until early and mis-dating screening_verified_at.
    return new Date(`${value}T00:00:00`).toISOString();
  }
}
